import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import {
    LsqApiServiceListSource,
    LsqDataSource,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    ClaimService,
    FeatureFlagService,
    LifecycleAware,
    WindowReferenceService,
    forLifeOf,
    getRouterLinkUrlById,
    ignoreNullish
} from '@lsq-frontend/common-ui';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES,
    SUPPLY_CHAIN_DIRECT_BUYER_INVOICES,
    SUPPLY_CHAIN_DIRECT_BUYER_PAY_LATER,
    atlas
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    GetCreditSummaryRequest,
    GetCreditSummaryService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/account-summary/get-credit-summary';
import {
    GetInvoiceSummaryRequest,
    GetInvoiceSummaryService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/account-summary/get-invoice-summary';
import {
    GetPaymentsSummaryRequest,
    GetPaymentsSummaryService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/account-summary/get-payments-summary';
import {
    ListBusinessUnitSummariesRequest,
    ListResponseBusinessUnitSummaryDto
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/account-summary/list-business-unit-summaries';
import { CreditSummaryDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/credit-summary-dto';
import { InvoiceSummaryDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/invoice-summary-dto';
import { PaymentsSummaryDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/payments-summary-dto';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    BuyerAgingSummaryDataSourceFactory,
    BuyerAgingSummaryGql,
    BuyerInvoiceStatus2,
    CurrencyCode,
    FacilityUtilizationLegacyRequest,
    FacilityUtilizationLegacyResponse,
    GetFacilityUtilizationEarlyPayment,
    GetFacilityUtilizationPayLater,
    GetFlexTermAvailabilityRequest,
    GetFlexTermAvailabilityResponse,
    GetFlexTermAvailabilityService,
    ListBusinessUnitSummariesService,
    ListInvoiceSummaryForBuyerRequest2,
    ListInvoiceSummaryForBuyerResponse,
    ListInvoiceSummaryOverviewService,
    ListSellerSummaryRequest,
    SupplierOverviewDataSource,
    SupplierOverviewDataSourceFactory,
    TopSuppliersByOpenBalance,
    TopSuppliersByOpenBalanceResponse
} from '@lsq-frontend/remote-services/financing-graphql';
import { Observable } from 'rxjs';
import { map, mapTo, pluck, startWith } from 'rxjs/operators';
import { SupplyChainDirectAccountService } from '../supply-chain-direct-account.service';
import { SupplyChainDirectBuyerAccountSharedService } from '../supply-chain-direct-buyer-account-shared.service';

@LifecycleAware()
@Component({
    selector: 'supply-chain-direct-buyer-account-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BuyerAccountOverviewComponent implements OnInit {
    public isBuyerUser$ = this._claimService.isOn(DashboardClaim.FinancingBuyerPortal);
    public isPayLaterEnabledForBuyer$ = this._buyerAccountSharedService.isPayLaterEnabled$;
    public payLaterFeatureFlag$ = this._featureFlagService.isOn(FeatureFlags.PayLater);
    public invoicesUrl$ = this.isBuyerUser$.pipe(
        map((isBuyer) =>
            isBuyer
                ? getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_BUYER_INVOICES)
                : getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES)
        )
    );
    public flexTermUrl = getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_BUYER_PAY_LATER);
    public screenWidthPx$ = this._windowService.resizeStream.pipe(pluck('width'));

    public readonly businessUnitsTableDataSource = new LsqApiServiceListSource<
        ListResponseBusinessUnitSummaryDto,
        ListBusinessUnitSummariesRequest
    >(
        this._listBusinessUnitsService,
        !this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)
            ? new ListBusinessUnitSummariesRequest({
                  filter: {
                      buyerKeys:
                          this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                              ?.value ?? []
                  },
                  pagination: {
                      sortColumn: 'buyerName',
                      sortDirection: SortDirectionFinancing.ASCENDING,
                      skip: 0,
                      take: 25
                  }
              })
            : undefined
    );
    private _paymentsSummaryRawData$ = this._getPaymentsSummaryService.dataStream.pipe(
        pluck('paymentsSummary'),
        ignoreNullish()
    );
    public paymentsSummaryDataSource = new LsqDataSource<PaymentsSummaryDto>({
        data$: this._paymentsSummaryRawData$.pipe(
            map((paymentsSummary) => {
                if (paymentsSummary === undefined) {
                    return {
                        amountDueNow: 0,
                        lateFees: 0,
                        totalBalance: 0
                    };
                }

                return paymentsSummary;
            })
        ),
        loading$: this._paymentsSummaryRawData$.pipe(mapTo(false), startWith(true))
    });
    private _invoiceSummaryRawData$ = this._getInvoiceSummaryService.dataStream.pipe(
        pluck('invoiceSummary'),
        ignoreNullish()
    );
    public invoiceSummaryDataSource = new LsqDataSource<InvoiceSummaryDto>({
        data$: this._invoiceSummaryRawData$.pipe(
            map((invoiceSummary) => {
                if (invoiceSummary === undefined) {
                    return {
                        totalEarlyPaid: {
                            cents: 0,
                            currencyCode: 'USD'
                        },
                        earlyPaidInvoiceCount: 0,
                        totalEligible: {
                            cents: 0,
                            currencyCode: 'USD'
                        },
                        eligibleInvoiceCount: 0,
                        totalIneligible: {
                            cents: 0,
                            currencyCode: 'USD'
                        },
                        ineligibleInvoiceCount: 0,
                        totalOpen: {
                            cents: 0,
                            currencyCode: 'USD'
                        },
                        totalOpenInvoiceCount: 0,
                        totalRevoked: {
                            cents: 0,
                            currencyCode: 'USD'
                        }
                    };
                }

                return invoiceSummary;
            })
        ),
        loading$: this._invoiceSummaryRawData$.pipe(mapTo(false), startWith(true))
    });
    private _creditSummaryRawData$ = this._getCreditSummaryService.dataStream.pipe(
        pluck('creditSummary'),
        ignoreNullish()
    );
    public creditSummaryDataSource = new LsqDataSource<CreditSummaryDto>({
        data$: this._creditSummaryRawData$.pipe(
            map((creditSummary) => {
                if (creditSummary === undefined) {
                    return {
                        creditLimit: {
                            cents: 0,
                            currencyCode: 'USD'
                        },
                        creditUsed: {
                            cents: 0,
                            currencyCode: 'USD'
                        },
                        creditRemaining: {
                            cents: 0,
                            currencyCode: 'USD'
                        }
                    };
                }

                return creditSummary;
            })
        ) as Observable<CreditSummaryDto>,
        loading$: this._creditSummaryRawData$.pipe(mapTo(false), startWith(true))
    });
    public agingOverviewDataSourceAllInvoices!: LsqDataSource<BuyerAgingSummaryGql>;
    public agingOverviewDataSourcePayLaterOnly$!: Observable<
        LsqDataSource<BuyerAgingSummaryGql> | undefined
    >;
    private _facilityUtilizationRawData$ = this._getFacilityUtilizationEarlyPayment.dataStream.pipe(
        ignoreNullish()
    );
    public facilityUtilizationData = new LsqDataSource<FacilityUtilizationLegacyResponse>({
        data$: this._facilityUtilizationRawData$.pipe(
            map((facility) => {
                if (facility === undefined) {
                    return new FacilityUtilizationLegacyResponse({
                        ok: true,
                        limit: {
                            cents: 0,
                            currencyCode: CurrencyCode.USD
                        },
                        utilized: {
                            cents: 0,
                            currencyCode: CurrencyCode.USD
                        },
                        available: {
                            cents: 0,
                            currencyCode: CurrencyCode.USD
                        }
                    });
                }

                return facility;
            })
        ),
        loading$: this._facilityUtilizationRawData$.pipe(mapTo(false), startWith(true))
    });
    private _flexTermFacilityUtilizationRawData$ =
        this._getFacilityUtilizationPayLater.dataStream.pipe(ignoreNullish());
    public flexTermFacilityUtilizationData = new LsqDataSource<FacilityUtilizationLegacyResponse>({
        data$: this._flexTermFacilityUtilizationRawData$.pipe(
            map((facility) => {
                if (facility === undefined) {
                    return new FacilityUtilizationLegacyResponse({
                        ok: true,
                        limit: {
                            cents: 0,
                            currencyCode: CurrencyCode.USD
                        },
                        utilized: {
                            cents: 0,
                            currencyCode: CurrencyCode.USD
                        },
                        available: {
                            cents: 0,
                            currencyCode: CurrencyCode.USD
                        }
                    });
                }

                return facility;
            })
        ),
        loading$: this._flexTermFacilityUtilizationRawData$.pipe(mapTo(false), startWith(true))
    });
    private _flexTermAvailabilityRawData$ = this._getFlexTermAvailability.dataStream.pipe(
        ignoreNullish()
    );
    public flexTermAvailabilityData$ = new LsqDataSource<GetFlexTermAvailabilityResponse>({
        data$: this._flexTermAvailabilityRawData$.pipe(
            map((facility) => {
                if (facility === undefined) {
                    return new GetFlexTermAvailabilityResponse({
                        ok: true,
                        openElegibleFlexTermPayableCount: 0,
                        eligibleAmount: {
                            cents: 0,
                            currencyCode: CurrencyCode.USD
                        }
                    });
                }

                return facility;
            })
        ),
        loading$: this._flexTermAvailabilityRawData$.pipe(mapTo(false), startWith(true))
    });
    public readonly topAgingInvoicesTableDataSource = new LsqApiServiceListSource<
        ListInvoiceSummaryForBuyerResponse,
        ListInvoiceSummaryForBuyerRequest2
    >(
        this._listInvoiceSummaryOverviewService,
        this._featureFlagService.isOnSync(FeatureFlags.PayLater)
            ? this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)
                ? new ListInvoiceSummaryForBuyerRequest2({
                      buyerInvoiceStatus: [
                          BuyerInvoiceStatus2.ELIGIBLE,
                          BuyerInvoiceStatus2.EARLY_PAID,
                          BuyerInvoiceStatus2.PENDING,
                          BuyerInvoiceStatus2.PAY_LATER,
                          BuyerInvoiceStatus2.EARLY_PAYMENT_SCHEDULED,
                          BuyerInvoiceStatus2.PAID,
                          BuyerInvoiceStatus2.FUNDING_REJECTED,
                          BuyerInvoiceStatus2.INELIGIBLE
                      ],
                      pagination: {
                          sortColumn: 'invoiceAmount',
                          sortDirection: SortDirectionFinancing.DESCENDING,
                          secondarySortColumn: 'sellerName',
                          secondarySortDirection: SortDirectionFinancing.DESCENDING,
                          skip: 0,
                          take: 5
                      }
                  })
                : new ListInvoiceSummaryForBuyerRequest2({
                      refBuyerKeys:
                          this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                              ?.value ?? [],
                      buyerInvoiceStatus: [
                          BuyerInvoiceStatus2.ELIGIBLE,
                          BuyerInvoiceStatus2.EARLY_PAID,
                          BuyerInvoiceStatus2.PENDING,
                          BuyerInvoiceStatus2.PAY_LATER,
                          BuyerInvoiceStatus2.EARLY_PAYMENT_SCHEDULED,
                          BuyerInvoiceStatus2.PAID,
                          BuyerInvoiceStatus2.FUNDING_REJECTED,
                          BuyerInvoiceStatus2.INELIGIBLE
                      ],
                      pagination: {
                          sortColumn: 'invoiceAmount',
                          sortDirection: SortDirectionFinancing.DESCENDING,
                          secondarySortColumn: 'sellerName',
                          secondarySortDirection: SortDirectionFinancing.DESCENDING,
                          skip: 0,
                          take: 5
                      }
                  })
            : this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)
            ? new ListInvoiceSummaryForBuyerRequest2({
                  buyerInvoiceStatus: [BuyerInvoiceStatus2.EARLY_PAID],
                  pagination: {
                      sortColumn: 'invoiceDueDate',
                      sortDirection: SortDirectionFinancing.ASCENDING,
                      secondarySortColumn: 'sellerName',
                      secondarySortDirection: SortDirectionFinancing.DESCENDING,
                      skip: 0,
                      take: 5
                  }
              })
            : undefined
    );
    public readonly suppliersTableDataSource = new LsqApiServiceListSource<
        TopSuppliersByOpenBalanceResponse,
        ListSellerSummaryRequest
    >(
        this._topSuppliersByOpenBalance,
        this._featureFlagService.isOnSync(FeatureFlags.PayLater)
            ? this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)
                ? new ListSellerSummaryRequest({
                      pagination: {
                          take: 5,
                          sortColumn: 'openInvoiceAmount',
                          sortDirection: SortDirectionFinancing.DESCENDING
                      }
                  })
                : new ListSellerSummaryRequest({
                      refBuyerKeys:
                          this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                              ?.value ?? [],
                      pagination: {
                          take: 5,
                          sortColumn: 'openInvoiceAmount',
                          sortDirection: SortDirectionFinancing.DESCENDING
                      }
                  })
            : this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)
            ? new ListSellerSummaryRequest({
                  pagination: {
                      take: 5,
                      sortColumn: 'openInvoiceAmount',
                      sortDirection: SortDirectionFinancing.DESCENDING
                  }
              })
            : undefined
    );
    public supplierOverviewDataSource!: SupplierOverviewDataSource;

    constructor(
        private _listBusinessUnitsService: ListBusinessUnitSummariesService,
        private _getInvoiceSummaryService: GetInvoiceSummaryService,
        private _getPaymentsSummaryService: GetPaymentsSummaryService,
        private _getCreditSummaryService: GetCreditSummaryService,
        private _supplyChainDirectAccountService: SupplyChainDirectAccountService,
        private _buyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService,
        private _claimService: ClaimService,
        private _featureFlagService: FeatureFlagService,
        private _getFacilityUtilizationEarlyPayment: GetFacilityUtilizationEarlyPayment,
        private _getFacilityUtilizationPayLater: GetFacilityUtilizationPayLater,
        private _getFlexTermAvailability: GetFlexTermAvailabilityService,
        private _userService: UserService,
        private _listInvoiceSummaryOverviewService: ListInvoiceSummaryOverviewService,
        private _topSuppliersByOpenBalance: TopSuppliersByOpenBalance,
        private _buyerAgingSummaryDataSourceFactory: BuyerAgingSummaryDataSourceFactory,
        private _supplierOverviewDataSourceFactory: SupplierOverviewDataSourceFactory,
        private _windowService: WindowReferenceService
    ) {}

    public ngOnInit(): void {
        if (!this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)) {
            this._setUpBusinessUnitFilterAsync();
        }

        if (!this._featureFlagService.isOnSync(FeatureFlags.PayLater)) {
            this._getPaymentsSummaryService.execute(
                new GetPaymentsSummaryRequest({
                    buyerKeys:
                        this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                            ?.value ?? []
                })
            );
            this.paymentsSummaryDataSource.connect();

            this._getInvoiceSummaryService.execute(
                new GetInvoiceSummaryRequest({
                    buyerKeys:
                        this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                            ?.value ?? []
                })
            );
            this.invoiceSummaryDataSource.connect();
        } else {
            if (!this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)) {
                //Facility buyer account
                this._getFacilityUtilizationEarlyPayment.execute(
                    new FacilityUtilizationLegacyRequest({
                        buyerAccountKeys: [this._getAlternateCompanyKey()]
                    })
                );
                this.facilityUtilizationData.connect();
                //Flex term facility buyer account
                this._getFacilityUtilizationPayLater.execute(
                    new FacilityUtilizationLegacyRequest({
                        buyerAccountKeys: [this._getAlternateCompanyKey()]
                    })
                );
                this.flexTermFacilityUtilizationData.connect();

                this._getFlexTermAvailability.execute(
                    new GetFlexTermAvailabilityRequest({
                        buyerAccountKeys: [this._getAlternateCompanyKey()]
                    })
                );
                this.flexTermAvailabilityData$.connect();
            } else {
                //Facility buyer
                this._getFacilityUtilizationEarlyPayment.execute(
                    new FacilityUtilizationLegacyRequest({
                        refBuyerKeys: [this._getAlternateCompanyKey()]
                    })
                );
                this.facilityUtilizationData.connect();

                //Flex Term Facility buyer
                this._getFacilityUtilizationPayLater.execute(
                    new FacilityUtilizationLegacyRequest({
                        refBuyerKeys: [this._getAlternateCompanyKey()]
                    })
                );
                this.flexTermFacilityUtilizationData.connect();

                this._getFlexTermAvailability.execute(
                    new GetFlexTermAvailabilityRequest({
                        refBuyerKeys: [this._getAlternateCompanyKey()]
                    })
                );
                this.flexTermAvailabilityData$.connect();
            }
        }

        this._getCreditSummaryService.execute(
            new GetCreditSummaryRequest({
                buyerKeys:
                    this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                        ?.value ?? []
            })
        );
        this.creditSummaryDataSource.connect();

        this.agingOverviewDataSourceAllInvoices = this._initAgingOverviewDataSourceAllInvoices();
        this.agingOverviewDataSourcePayLaterOnly$ =
            this._initAgingOverviewDataSourcePayLaterOnly$();
        this.supplierOverviewDataSource = this._initSupplierOverviewDataSource();
    }

    public ngOnDestroy(): void {
        this.paymentsSummaryDataSource.disconnect();
        this.invoiceSummaryDataSource.disconnect();
        this.creditSummaryDataSource.disconnect();
        this.facilityUtilizationData.disconnect();
        this.flexTermFacilityUtilizationData.disconnect();
        this.agingOverviewDataSourceAllInvoices.disconnect();
        this.agingOverviewDataSourcePayLaterOnly$.subscribe((ds) => ds?.disconnect());
        this.supplierOverviewDataSource.disconnect();
        // Don't need to connect and disconnect the businessUnitsTableDataSource because
        // `lsq-data-table` handles that internally (because CdkTable handles it internally).
    }

    private _initAgingOverviewDataSourceAllInvoices(
        buyerKeys?: string[]
    ): LsqDataSource<BuyerAgingSummaryGql> {
        return this._buyerAgingSummaryDataSourceFactory.createBuyerAgingSummaryDataSource(
            buyerKeys ??
                this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')?.value ??
                []
        );
    }

    private _initAgingOverviewDataSourcePayLaterOnly$(
        buyerKeys?: string[]
    ): Observable<LsqDataSource<BuyerAgingSummaryGql> | undefined> {
        return this._buyerAccountSharedService.isPayLaterEnabled$.pipe(
            map((payLaterEnabled) =>
                payLaterEnabled
                    ? this._buyerAgingSummaryDataSourceFactory.createBuyerAgingSummaryDataSource(
                          buyerKeys ??
                              this._supplyChainDirectAccountService.buyerKeysFormGroup.get(
                                  'buyerKeys'
                              )?.value ??
                              [],
                          true
                      )
                    : undefined
            )
        );
    }

    private _initSupplierOverviewDataSource(buyerKeys?: string[]): SupplierOverviewDataSource {
        return this._supplierOverviewDataSourceFactory.createSupplierOverviewDataSource({
            buyerKeys:
                buyerKeys ??
                this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')?.value ??
                []
        });
    }

    private _setUpBusinessUnitFilterAsync(): void {
        this._supplyChainDirectAccountService.buyerKeysFormGroup
            .get('buyerKeys')
            ?.valueChanges.pipe(forLifeOf(this))
            .subscribe((buyerKeys: string[]) => {
                this._getPaymentsSummaryService.execute(
                    new GetPaymentsSummaryRequest({ buyerKeys })
                );
                this._getInvoiceSummaryService.execute(new GetInvoiceSummaryRequest({ buyerKeys }));
                this._getCreditSummaryService.execute(new GetCreditSummaryRequest({ buyerKeys }));
                this.businessUnitsTableDataSource.dispatchRequest({
                    filter: {
                        ...this.businessUnitsTableDataSource.request.filter,
                        buyerKeys
                    }
                });

                if (this._featureFlagService.isOnSync(FeatureFlags.PayLater)) {
                    this._getFacilityUtilizationEarlyPayment.execute(
                        new FacilityUtilizationLegacyRequest({
                            refBuyerKeys: buyerKeys,
                            buyerAccountKeys: [this._getAlternateCompanyKey()]
                        })
                    );
                    this._getFacilityUtilizationPayLater.execute(
                        new FacilityUtilizationLegacyRequest({
                            refBuyerKeys: buyerKeys,
                            buyerAccountKeys: [this._getAlternateCompanyKey()]
                        })
                    );
                    this.topAgingInvoicesTableDataSource.dispatchRequest({
                        ...this.topAgingInvoicesTableDataSource.request,
                        refBuyerKeys: buyerKeys,
                        buyerAccountKeys: [this._getAlternateCompanyKey()]
                    });

                    this.suppliersTableDataSource.dispatchRequest({
                        ...this.suppliersTableDataSource.request,
                        refBuyerKeys: buyerKeys
                    });
                }

                this.agingOverviewDataSourceAllInvoices =
                    this._initAgingOverviewDataSourceAllInvoices(buyerKeys);
                this.agingOverviewDataSourcePayLaterOnly$ =
                    this._initAgingOverviewDataSourcePayLaterOnly$(buyerKeys);
                this.supplierOverviewDataSource.dispatchRequest({ buyerKeys });
            });
    }

    private _getAlternateCompanyKey(): string {
        return this._userService.getAlternateCompanyKey();
    }
}
